/**
 * Secure Auth Storage Utilities
 * 
 * Note: For better security, consider using httpOnly cookies
 * managed by the backend instead of localStorage.
 * 
 * This module provides a centralized way to handle auth storage
 * making it easier to migrate to more secure methods later.
 */

const AUTH_TOKEN_KEY = 'token';
const USER_DATA_KEY = 'user';
const PORTAL_TOKEN_KEY = 'portal_token';
const PORTAL_CUSTOMER_KEY = 'portal_customer';

// Session timeout in milliseconds (24 hours)
const SESSION_TIMEOUT = 24 * 60 * 60 * 1000;

/**
 * Store admin auth token
 */
export const setAuthToken = (token) => {
  try {
    const data = {
      token,
      timestamp: Date.now()
    };
    sessionStorage.setItem(AUTH_TOKEN_KEY, JSON.stringify(data));
    // Fallback to localStorage for persistence across tabs
    localStorage.setItem(AUTH_TOKEN_KEY, token);
  } catch (e) {
    console.error('Failed to store auth token:', e);
  }
};

/**
 * Get admin auth token
 */
export const getAuthToken = () => {
  try {
    // Try sessionStorage first
    const sessionData = sessionStorage.getItem(AUTH_TOKEN_KEY);
    if (sessionData) {
      const { token, timestamp } = JSON.parse(sessionData);
      // Check if session is still valid
      if (Date.now() - timestamp < SESSION_TIMEOUT) {
        return token;
      }
      // Session expired, clear it
      clearAuthData();
      return null;
    }
    // Fallback to localStorage
    return localStorage.getItem(AUTH_TOKEN_KEY);
  } catch (e) {
    return localStorage.getItem(AUTH_TOKEN_KEY);
  }
};

/**
 * Store user data
 */
export const setUserData = (user) => {
  try {
    // Don't store sensitive data like passwords
    const safeUser = { ...user };
    delete safeUser.password;
    delete safeUser.password_hash;
    
    localStorage.setItem(USER_DATA_KEY, JSON.stringify(safeUser));
  } catch (e) {
    console.error('Failed to store user data:', e);
  }
};

/**
 * Get user data
 */
export const getUserData = () => {
  try {
    const data = localStorage.getItem(USER_DATA_KEY);
    return data ? JSON.parse(data) : null;
  } catch (e) {
    return null;
  }
};

/**
 * Clear all auth data
 */
export const clearAuthData = () => {
  sessionStorage.removeItem(AUTH_TOKEN_KEY);
  localStorage.removeItem(AUTH_TOKEN_KEY);
  localStorage.removeItem(USER_DATA_KEY);
};

/**
 * Store portal customer token
 */
export const setPortalToken = (token) => {
  try {
    sessionStorage.setItem(PORTAL_TOKEN_KEY, token);
    localStorage.setItem(PORTAL_TOKEN_KEY, token);
  } catch (e) {
    console.error('Failed to store portal token:', e);
  }
};

/**
 * Get portal customer token
 */
export const getPortalToken = () => {
  try {
    return sessionStorage.getItem(PORTAL_TOKEN_KEY) || localStorage.getItem(PORTAL_TOKEN_KEY);
  } catch (e) {
    return localStorage.getItem(PORTAL_TOKEN_KEY);
  }
};

/**
 * Store portal customer data
 */
export const setPortalCustomer = (customer) => {
  try {
    const safeCustomer = { ...customer };
    delete safeCustomer.password;
    localStorage.setItem(PORTAL_CUSTOMER_KEY, JSON.stringify(safeCustomer));
  } catch (e) {
    console.error('Failed to store portal customer:', e);
  }
};

/**
 * Get portal customer data
 */
export const getPortalCustomer = () => {
  try {
    const data = localStorage.getItem(PORTAL_CUSTOMER_KEY);
    return data ? JSON.parse(data) : null;
  } catch (e) {
    return null;
  }
};

/**
 * Clear all portal auth data
 */
export const clearPortalData = () => {
  sessionStorage.removeItem(PORTAL_TOKEN_KEY);
  localStorage.removeItem(PORTAL_TOKEN_KEY);
  localStorage.removeItem(PORTAL_CUSTOMER_KEY);
};

/**
 * Check if admin is authenticated
 */
export const isAuthenticated = () => {
  return !!getAuthToken();
};

/**
 * Check if portal customer is authenticated
 */
export const isPortalAuthenticated = () => {
  return !!getPortalToken();
};

export default {
  setAuthToken,
  getAuthToken,
  setUserData,
  getUserData,
  clearAuthData,
  setPortalToken,
  getPortalToken,
  setPortalCustomer,
  getPortalCustomer,
  clearPortalData,
  isAuthenticated,
  isPortalAuthenticated
};
